import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { CardModule } from 'primeng/card';
import { MessageModule } from 'primeng/message';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../core/services/auth.service';
import { KeycloakService } from '../../core/services/keycloak.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    PasswordModule,
    CardModule,
    MessageModule,
    TranslateModule,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly translateService = inject(TranslateService);
  private readonly authService = inject(AuthService);
  private readonly keycloakService = inject(KeycloakService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly isLoading = signal(false);
  readonly errorMessage = signal('');
  readonly translationsLoaded = signal(false);
  readonly showForm = signal(false);

  loginForm: FormGroup;

  constructor() {
    this.loginForm = this.fb.group({
      username: ['', [Validators.required, Validators.minLength(3)]],
      password: ['', [Validators.required, Validators.minLength(6)]],
    });
  }

  async ngOnInit(): Promise<void> {
    try {
      const keycloakReady = await this.waitForKeycloakInit();
      if (!keycloakReady) {
        this.showKeycloakNotReadyError();
        return;
      }

      this.authService.ensureListenersSetup();
      await this.trySyncWithKeycloak();

      const isLoggedIn = this.authService.isLoggedIn();
      const isSessionValid = this.authService.isSessionValid();
      const isKeycloakAuthenticated =
        this.authService.isKeycloakReady() &&
        this.keycloakService.isAuthenticated();

      if (isLoggedIn && isSessionValid) {
        this.redirectToReturnUrl();
        return;
      }

      if (isLoggedIn && !isSessionValid && isKeycloakAuthenticated) {
        const redirected = await this.tryRefreshAndRedirect();
        if (redirected) return;
      }

      if (isKeycloakAuthenticated && !isLoggedIn) {
        const redirected = await this.trySyncAndRedirect(
          '[Login] Keycloak authenticated but Angular not synced, retrying sync...',
          '[Login] Retry sync failed:',
        );
        if (redirected) return;
      }

      if (isKeycloakAuthenticated) {
        await this.trySyncAndRedirect(
          '[Login] Retry sync...',
          '[Login] Final sync failed:',
        );
        this.redirectToReturnUrl();
        return;
      }

      this.triggerKeycloakLogin();
    } catch (error) {
      console.error('[Login] Unexpected error in ngOnInit:', error);
      this.errorMessage.set(
        this.translateService.instant('login.error.unexpected') ||
          'An unexpected error occurred. Please refresh the page.',
      );
      this.showForm.set(true);
      this.cdr.detectChanges();
    }
  }

  private showKeycloakNotReadyError(): void {
    console.error('[Login] Keycloak failed to initialize within timeout');
    this.errorMessage.set(
      this.translateService.instant('login.error.keycloak_not_ready') ||
        'Keycloak authentication service is not available. Please refresh the page.',
    );
    this.showForm.set(true);
    this.cdr.detectChanges();
  }

  private async trySyncWithKeycloak(): Promise<void> {
    try {
      await Promise.race([
        this.authService.syncWithKeycloak(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout')), 5000),
        ),
      ]);
    } catch (error) {
      console.error('[Login] syncWithKeycloak timeout or error:', error);
    }
  }

  private redirectToReturnUrl(): void {
    const queryReturnUrl = this.route.snapshot.queryParams['returnUrl'];
    const storedReturnUrl = this.getStoredReturnUrl();
    const returnUrl = queryReturnUrl || storedReturnUrl || '/home';
    if (storedReturnUrl) {
      this.clearStoredReturnUrl();
    }
    globalThis.location.href = globalThis.location.origin + returnUrl;
  }

  private async tryRefreshAndRedirect(): Promise<boolean> {
    try {
      const newToken = await this.keycloakService.updateToken(0);
      if (newToken) {
        await this.authService.syncWithKeycloak();
        console.warn('[Login] Token refreshed, redirecting...');
        this.redirectToReturnUrl();
        return true;
      } else {
        console.warn(
          '[Login] Token refresh returned null, refresh token may be expired',
        );
      }
    } catch (error) {
      console.error('[Login] Token refresh failed:', error);
    }
    return false;
  }

  private async trySyncAndRedirect(
    logMessage: string,
    errorMessage: string,
  ): Promise<boolean> {
    console.warn(logMessage);
    try {
      await this.authService.syncWithKeycloak();
      if (this.authService.isLoggedIn()) {
        this.redirectToReturnUrl();
        return true;
      }
    } catch (error) {
      console.error(errorMessage, error);
    }
    return false;
  }

  private triggerKeycloakLogin(): void {
    this.showForm.set(false);
    this.cdr.detectChanges();

    if (this.authService.isLoggedIn()) {
      return;
    }

    const queryReturnUrl = this.route.snapshot.queryParams['returnUrl'];
    const storedReturnUrl = this.getStoredReturnUrl();
    const returnUrl = queryReturnUrl || storedReturnUrl || '/home';

    if (!this.authService.isKeycloakReady()) {
      console.error('[Login] Keycloak instance not available for login');
      this.errorMessage.set(
        this.translateService.instant('login.error.keycloak_not_ready') ||
          'Keycloak authentication service is not available. Please refresh the page.',
      );
      this.showForm.set(true);
      this.cdr.detectChanges();
      return;
    }

    try {
      this.authService.login(returnUrl);
      setTimeout(() => {
        if (this.router.url.includes('/login')) {
          console.warn(
            '[Login] Still on login page after 1 second, retrying redirect...',
          );
          try {
            this.authService.login(returnUrl);
          } catch (error) {
            console.error('[Login] Retry failed:', error);
            this.showForm.set(true);
            this.cdr.detectChanges();
          }
        }
      }, 1000);
    } catch (error) {
      console.error('[Login] Error calling authService.login:', error);
      this.errorMessage.set(
        this.translateService.instant('login.error.unexpected') ||
          'An unexpected error occurred. Please try again.',
      );
      this.showForm.set(true);
      this.cdr.detectChanges();
    }
  }

  /**
   * Wait for Keycloak instance to be initialized
   * @returns true if Keycloak is ready, false if timeout
   */
  private async waitForKeycloakInit(): Promise<boolean> {
    const maxAttempts = 50; // 5 seconds total (50 * 100ms)
    let attempts = 0;

    while (attempts < maxAttempts) {
      if (this.authService.isKeycloakReady()) {
        return true;
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;
    }

    console.warn(
      `[Login] Keycloak initialization timeout after ${maxAttempts} attempts`,
    );
    return false;
  }

  /**
   * Form submission → validate → trigger Keycloak login
   * Username/password fields are NOT used for actual authentication
   */
  onSubmit(): void {
    // Form validation must still work for UI purposes
    if (this.loginForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set('');

    try {
      // Form is valid → trigger Keycloak login redirect
      // Ignore actual form username/password values
      // Get returnUrl from query params, or from localStorage (stored before logout), or default to /home
      const queryReturnUrl = this.route.snapshot.queryParams['returnUrl'];
      const storedReturnUrl = this.getStoredReturnUrl();
      const returnUrl = queryReturnUrl || storedReturnUrl || '/home';
      this.authService.login(returnUrl); // KEYCLOAK REDIRECT

      // No double login, no local login logic
      // User will be redirected to Keycloak login page
    } catch (error) {
      console.error('[Login] Error triggering Keycloak login:', error);
      this.errorMessage.set(
        this.translateService.instant('login.error.unexpected'),
      );
      this.isLoading.set(false);
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.loginForm.controls).forEach((key) => {
      const control = this.loginForm.get(key);
      control?.markAsTouched();
    });
  }

  getFieldError(fieldName: string): string {
    const control = this.loginForm.get(fieldName);

    if (control?.errors && control.touched) {
      if (control.errors['required']) {
        return this.getTranslationWithFallback(
          'login.validation.required',
          'This field is required',
        );
      }
      if (control.errors['minlength']) {
        const requiredLength = control.errors['minlength'].requiredLength;
        return this.getTranslationWithFallback(
          'login.validation.min_length',
          `Minimum ${requiredLength} characters required`,
          { length: requiredLength },
        );
      }
    }

    return '';
  }

  private getTranslationWithFallback(
    key: string,
    fallback: string,
    params?: any,
  ): string {
    try {
      const translation = this.translateService.instant(key, params);
      return translation === key ? fallback : translation;
    } catch {
      return fallback;
    }
  }

  hasFieldError(fieldName: string): boolean {
    const control = this.loginForm.get(fieldName);
    return !!(control?.errors && control.touched);
  }

  /**
   * Get stored returnUrl from localStorage (stored before logout)
   */
  private getStoredReturnUrl(): string | null {
    try {
      // Check new key first, then fallback to old key for migration
      return (
        localStorage.getItem('logout_return_url') ||
        localStorage.getItem('logout_returnUrl')
      );
    } catch (error) {
      console.warn('[Login] Failed to get stored returnUrl:', error);
      return null;
    }
  }

  /**
   * Clear stored returnUrl from localStorage
   */
  private clearStoredReturnUrl(): void {
    try {
      localStorage.removeItem('logout_return_url');
      localStorage.removeItem('logout_returnUrl'); // Remove old key for migration
    } catch (error) {
      console.warn('[Login] Failed to clear stored returnUrl:', error);
    }
  }
}
